/**
 * EF-CLIENT-ROLE-FOUNDATION-V1: Client Team Page
 * EF-CLIENT-PORTAL-V1-STEP3: Added "Add Contact" functionality
 * 
 * Allows client portal admins to view, add, and invite team members.
 * Viewers see a message that this feature requires admin access.
 */
import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  ShieldOff,
  Loader2,
  Clock,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  Phone,
  Plus,
  KeyRound,
  Trash2,
  UserCog,
} from "lucide-react";
import { useClientPortal, CLIENT_ROLE } from "../../contexts/ClientPortalContext";
import { useI18n } from "../../i18n/I18nProvider";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Status badge for team member portal access
 */
const PortalStatusBadge = ({ status, onboardingCompleted }) => {
  const configs = {
    invited: {
      className: "border-amber-500/50 text-amber-400 bg-amber-500/10",
      icon: Clock,
      label: "Invited"
    },
    active: {
      className: "border-green-500/50 text-green-400 bg-green-500/10",
      icon: CheckCircle2,
      label: "Active"
    },
    inactive: {
      className: "border-gray-500/50 text-gray-400 bg-gray-500/10",
      icon: ShieldOff,
      label: "Inactive"
    },
    pending: {
      className: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      icon: Clock,
      label: "Pending"
    }
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`text-xs py-0 h-5 ${config.className}`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
};

export default function ClientTeamPage() {
  const { getToken } = useOutletContext();
  const { isAdmin, isViewer, canManageUsers } = useClientPortal();
  const { t } = useI18n();
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitingContact, setInvitingContact] = useState(null);
  
  // EF-CLIENT-PORTAL-V1-STEP3: Add Contact modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    role: "",
  });

  // EVENTSFLO-CLIENT-PORTAL-TEAM-MANAGEMENT-CLOSEOUT-V1: action state
  const [actingContactId, setActingContactId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Fetch team contacts (admin only)
  const fetchContacts = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/client/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 403) {
          // Expected for viewers - not an error
          setError("admin_required");
          setLoading(false);
          return;
        }
        throw new Error("Failed to fetch contacts");
      }

      const data = await response.json();
      setContacts(data.data || []);
    } catch (err) {
      console.error("Contacts fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Invite contact to portal
  const handleInvite = async (contactId, contactName) => {
    const token = getToken();
    if (!token) return;

    setInvitingContact(contactId);
    try {
      const response = await fetch(`${API_URL}/api/client/invite-contact/${contactId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to send invitation");
      }

      const data = await response.json();
      toast.success(data.message || `Invitation sent to ${contactName}`);
      fetchContacts(); // Refresh list
    } catch (err) {
      toast.error(err.message);
    } finally {
      setInvitingContact(null);
    }
  };

  // EF-CLIENT-PORTAL-V1-STEP3: Add Contact handler
  const handleAddContact = async (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;

    // Validation
    if (!newContact.first_name.trim() || !newContact.last_name.trim() || !newContact.email.trim()) {
      toast.error(t("clientPortal:team.nameEmailRequired"));
      return;
    }

    setAddingContact(true);
    try {
      const response = await fetch(`${API_URL}/api/client/contacts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newContact),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to add contact");
      }

      const data = await response.json();
      toast.success(data.message || "Contact added successfully");
      setShowAddModal(false);
      setNewContact({ first_name: "", last_name: "", email: "", mobile: "", role: "" });
      fetchContacts(); // Refresh list
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddingContact(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchContacts();
    } else {
      setLoading(false);
      setError("admin_required");
    }
  }, [isAdmin, fetchContacts]);

  // EVENTSFLO-CLIENT-PORTAL-TEAM-MANAGEMENT-CLOSEOUT-V1: action handlers

  const callContactAction = async (contactId, path, method, body, successMsg) => {
    const token = getToken();
    if (!token) return;
    setActingContactId(`${contactId}:${path}`);
    try {
      const response = await fetch(
        `${API_URL}/api/client/contacts/${contactId}${path}`,
        {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: body ? JSON.stringify(body) : undefined,
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || "Action failed");
      }
      toast.success(data.message || successMsg);
      fetchContacts();
      return data;
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActingContactId(null);
    }
  };

  const handleResendInvite = (contactId, contactName) =>
    callContactAction(
      contactId,
      "/resend-invite",
      "POST",
      null,
      `Invitation resent to ${contactName}`
    );

  const handlePasswordReset = (contactId, contactName) =>
    callContactAction(
      contactId,
      "/password-reset",
      "POST",
      null,
      `Password reset email sent to ${contactName}`
    );

  const handleRoleChange = (contactId, newPortalRole, contactName) =>
    callContactAction(
      contactId,
      "",
      "PATCH",
      { portal_role: newPortalRole },
      `${contactName} is now ${newPortalRole}`
    );

  const handleDeactivate = async (contactId, contactName) => {
    await callContactAction(
      contactId,
      "",
      "DELETE",
      null,
      `${contactName} removed from portal`
    );
    setConfirmDelete(null);
  };

  // Viewer access denied view
  if (isViewer || error === "admin_required") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Team
          </h1>
          <p className="text-muted-foreground text-sm">
            Manage your organization's portal access
          </p>
        </div>

        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <ShieldOff className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h2 className="text-lg font-semibold mb-2">{t("clientPortal:team.adminAccessRequired")}</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              You need administrator permissions to view and manage team members.
              Contact your organization's admin if you need access to this feature.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error state
  if (error && error !== "admin_required") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Team
          </h1>
        </div>
        <Card className="border-destructive/50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto text-destructive mb-3" />
            <p className="text-destructive">{error}</p>
            <Button variant="outline" className="mt-4" onClick={fetchContacts}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin view - show contacts list
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Team
          </h1>
          <p className="text-muted-foreground text-sm">
            Manage your organization's portal access ({contacts.length} contact{contacts.length !== 1 ? 's' : ''})
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* EF-CLIENT-PORTAL-V1-STEP3: Add Contact button (Admin only) */}
          <Button
            size="sm"
            onClick={() => setShowAddModal(true)}
            data-testid="add-contact-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Contact
          </Button>
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        </div>
      </div>

      {/* Contacts List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Organization Contacts</CardTitle>
          <CardDescription>
            Invite contacts to access the client portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          {contacts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No contacts found</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setShowAddModal(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add your first contact
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                  data-testid={`team-contact-${contact.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {contact.first_name} {contact.last_name}
                      </span>
                      {contact.role && (
                        <span className="text-xs text-muted-foreground">({contact.role})</span>
                      )}
                      {contact.has_portal_access && (
                        <PortalStatusBadge
                          status={contact.portal_status}
                          onboardingCompleted={contact.onboarding_completed}
                        />
                      )}
                      {contact.portal_role === "admin" && (
                        <Badge variant="outline" className="text-xs py-0 h-5 bg-primary/10 text-primary border-primary/20">
                          <Shield className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {contact.email}
                      </span>
                    </div>
                  </div>

                  {/* Invite Button */}
                  {canManageUsers && !contact.has_portal_access && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInvite(contact.id, `${contact.first_name} ${contact.last_name}`)}
                      disabled={invitingContact === contact.id}
                      data-testid={`invite-contact-${contact.id}`}
                    >
                      {invitingContact === contact.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Invite
                        </>
                      )}
                    </Button>
                  )}

                  {/* EVENTSFLO-CLIENT-PORTAL-TEAM-MANAGEMENT-CLOSEOUT-V1: action cluster */}
                  {canManageUsers && contact.has_portal_access && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Resend invitation"
                        onClick={() =>
                          handleResendInvite(
                            contact.id,
                            `${contact.first_name} ${contact.last_name}`
                          )
                        }
                        disabled={actingContactId === `${contact.id}:/resend-invite`}
                        data-testid={`resend-invite-${contact.id}`}
                      >
                        {actingContactId === `${contact.id}:/resend-invite` ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                      {contact.onboarding_completed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          title={t("clientPortal:team.sendResetEmail")}
                          onClick={() =>
                            handlePasswordReset(
                              contact.id,
                              `${contact.first_name} ${contact.last_name}`
                            )
                          }
                          disabled={actingContactId === `${contact.id}:/password-reset`}
                          data-testid={`password-reset-${contact.id}`}
                        >
                          {actingContactId === `${contact.id}:/password-reset` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <KeyRound className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        title={
                          contact.portal_role === "admin"
                            ? "Demote to viewer"
                            : "Promote to admin"
                        }
                        onClick={() =>
                          handleRoleChange(
                            contact.id,
                            contact.portal_role === "admin" ? "viewer" : "admin",
                            `${contact.first_name} ${contact.last_name}`
                          )
                        }
                        disabled={actingContactId === `${contact.id}:`}
                        data-testid={`toggle-role-${contact.id}`}
                      >
                        {actingContactId === `${contact.id}:` ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <UserCog className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        title="Remove portal access"
                        onClick={() =>
                          setConfirmDelete({
                            id: contact.id,
                            name: `${contact.first_name} ${contact.last_name}`,
                          })
                        }
                        data-testid={`deactivate-contact-${contact.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* EF-CLIENT-PORTAL-V1-STEP3: Add Contact Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md" data-testid="add-contact-modal">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
            <DialogDescription>
              Add a new contact to your organization. You can invite them to the portal after adding.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddContact} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t("clientPortal:team.firstName")}</Label>
                <Input
                  id="first_name"
                  placeholder="John"
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                  required
                  data-testid="add-contact-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{t("clientPortal:team.lastName")}</Label>
                <Input
                  id="last_name"
                  placeholder="Doe"
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                  required
                  data-testid="add-contact-lastname"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("clientPortal:team.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="john.doe@company.com"
                  className="pl-9"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  required
                  data-testid="add-contact-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile">{t("clientPortal:team.phoneOptional")}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="mobile"
                  type="tel"
                  placeholder="+33 6 12 34 56 78"
                  className="pl-9"
                  value={newContact.mobile}
                  onChange={(e) => setNewContact({ ...newContact, mobile: e.target.value })}
                  data-testid="add-contact-mobile"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">{t("clientPortal:team.roleOptional")}</Label>
              <Input
                id="role"
                placeholder="Project Manager"
                value={newContact.role}
                onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                data-testid="add-contact-role"
              />
            </div>
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addingContact} data-testid="add-contact-submit">
                {addingContact ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contact
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* EVENTSFLO-CLIENT-PORTAL-TEAM-MANAGEMENT-CLOSEOUT-V1: Deactivate confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="deactivate-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Remove portal access?
            </DialogTitle>
            <DialogDescription>
              <strong>{confirmDelete?.name}</strong> will lose access to the
              client portal. Their contact record will be deactivated. You can
              re-invite them later if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              data-testid="deactivate-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmDelete &&
                handleDeactivate(confirmDelete.id, confirmDelete.name)
              }
              disabled={actingContactId?.startsWith(confirmDelete?.id + ":")}
              data-testid="deactivate-confirm-btn"
            >
              {actingContactId === `${confirmDelete?.id}:` ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Remove access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
